import {
  getVendiaResourceFromResourceString,
  matchesVendiaResource,
  VendiaResourceRepresentation,
} from '@vendia/management-api-rbac'
import { ActionEnum, Capability, Role } from '@vendia/management-api-types'

export type AccessSource = 'org-wide' | 'uni-wide' | 'node-wide' | 'resource-specific' | 'none'

export type AccessLevel = 'read' | 'write' | 'none'

export interface ResourceAccessInfo {
  hasAccess: boolean
  source: AccessSource
  /** The access level (read vs write) - primarily used for entity access */
  accessLevel: AccessLevel
  /** Can only revoke resource-specific access, not inherited */
  canRevoke: boolean
}

export interface RoleWithResourceAccess {
  role: Role
  accessInfo: ResourceAccessInfo
}

/**
 * Build a VendiaResource string for a specific resource (entity or table)
 */
export function buildResource({
  orgId,
  uniName,
  nodeName,
  entityName,
}: {
  orgId: string
  uniName: string
  nodeName: string
  entityName: string
}): VendiaResourceRepresentation {
  return `VendiaResource(${orgId}/${uniName}/${nodeName}/${entityName}/*)` as VendiaResourceRepresentation
}

/**
 * Build a VendiaResource string for table metadata access (used for DATA_READ)
 * This grants access to the Vendia_Table entity which contains metadata for all tables
 */
export function buildTableMetadataResource({
  orgId,
  uniName,
  nodeName,
}: {
  orgId: string
  uniName: string
  nodeName: string
}): VendiaResourceRepresentation {
  return `VendiaResource(${orgId}/${uniName}/${nodeName}/Vendia_Table/*)` as VendiaResourceRepresentation
}

/**
 * Determine the access source by checking which resource pattern grants access
 */
function getAccessSource(matchingResource: string, targetResource: VendiaResourceRepresentation): AccessSource {
  const targetRes = getVendiaResourceFromResourceString(targetResource)
  const matchRes = getVendiaResourceFromResourceString(matchingResource as VendiaResourceRepresentation)

  // Check specificity by comparing resource patterns
  // Org-wide: VendiaResource(orgId/*) - uniName is wildcard
  if (matchRes.uniName === '*') {
    return 'org-wide'
  }

  // Uni-wide: VendiaResource(orgId/uniName/*) - nodeName is wildcard
  if (matchRes.nodeName === '*') {
    return 'uni-wide'
  }

  // Node-wide: VendiaResource(orgId/uniName/nodeName/*) - entityName is wildcard
  if (matchRes.entityName === '*') {
    return 'node-wide'
  }

  // Resource-specific: exact match on orgId/uniName/nodeName/entityName
  if (
    matchRes.orgId === targetRes.orgId &&
    matchRes.uniName === targetRes.uniName &&
    matchRes.nodeName === targetRes.nodeName &&
    matchRes.entityName === targetRes.entityName
  ) {
    return 'resource-specific'
  }

  // If we can't determine the level, return 'none'
  return 'none'
}

/**
 * Determine the access level based on which action grants access
 * DATA_ALL / TABLE_ALL = write, DATA_READ / TABLE_READ = read
 */
function getAccessLevel(action: ActionEnum): AccessLevel {
  if (action === ActionEnum.DataAll || action === ActionEnum.TableAll) {
    return 'write'
  }
  if (action === ActionEnum.DataRead || action === ActionEnum.TableRead) {
    return 'read'
  }
  return 'none'
}

/**
 * Get detailed access info for a role on a specific resource
 * @param role - The role to check
 * @param resource - The VendiaResource to check access for
 * @param actionsToCheck - Array of actions that grant access (e.g., [ActionEnum.DataRead, ActionEnum.DataAll])
 */
export function getResourceAccessInfo(
  role: Role,
  resource: VendiaResourceRepresentation,
  actionsToCheck: ActionEnum[],
): ResourceAccessInfo {
  if (!role?.capabilities) {
    return { hasAccess: false, source: 'none', accessLevel: 'none', canRevoke: false }
  }

  const targetResource = getVendiaResourceFromResourceString(resource)

  // Collect all matching access info, then return the one with highest access level
  let bestMatch: ResourceAccessInfo | null = null

  for (const capability of role.capabilities) {
    if (!actionsToCheck.includes(capability.action)) {
      continue
    }

    for (const capResource of capability.resources) {
      if (!capResource.startsWith('VendiaResource(')) {
        continue
      }

      const vendiaResource = getVendiaResourceFromResourceString(capResource as VendiaResourceRepresentation)

      if (matchesVendiaResource(vendiaResource, targetResource)) {
        const source = getAccessSource(capResource, resource)
        const accessLevel = getAccessLevel(capability.action)
        const match: ResourceAccessInfo = {
          hasAccess: true,
          source,
          accessLevel,
          canRevoke: source === 'resource-specific',
        }

        // Prefer write over read access
        if (!bestMatch || (accessLevel === 'write' && bestMatch.accessLevel !== 'write')) {
          bestMatch = match
        }
      }
    }
  }

  return bestMatch ?? { hasAccess: false, source: 'none', accessLevel: 'none', canRevoke: false }
}

/**
 * Get all roles that have access to a specific resource
 * @param roles - Array of roles to check
 * @param resource - The VendiaResource to check access for
 * @param actionsToCheck - Array of actions that grant access
 */
export function getRolesWithResourceAccess(
  roles: Role[],
  resource: VendiaResourceRepresentation,
  actionsToCheck: ActionEnum[],
): RoleWithResourceAccess[] {
  return roles
    .map((role) => ({
      role,
      accessInfo: getResourceAccessInfo(role, resource, actionsToCheck),
    }))
    .filter((item) => item.accessInfo.hasAccess)
}

/**
 * Check if a role already has data access that covers the specified node
 * (via DATA_READ or DATA_ALL at org, uni, node, or Vendia_Table level)
 */
function hasExistingDataAccess(capabilities: Capability[], orgId: string, uniName: string, nodeName: string): boolean {
  const dataActions = [ActionEnum.DataRead, ActionEnum.DataAll]

  for (const capability of capabilities) {
    if (!dataActions.includes(capability.action)) {
      continue
    }

    for (const resource of capability.resources) {
      if (!resource.startsWith('VendiaResource(')) {
        continue
      }

      const vendiaResource = getVendiaResourceFromResourceString(resource as VendiaResourceRepresentation)

      // Org-wide access
      if (vendiaResource.orgId === orgId && vendiaResource.uniName === '*') {
        return true
      }

      // Uni-wide access
      if (vendiaResource.orgId === orgId && vendiaResource.uniName === uniName && vendiaResource.nodeName === '*') {
        return true
      }

      // Node-wide access
      if (
        vendiaResource.orgId === orgId &&
        vendiaResource.uniName === uniName &&
        vendiaResource.nodeName === nodeName &&
        vendiaResource.entityName === '*'
      ) {
        return true
      }

      // Vendia_Table specific access (already has metadata access for this node)
      if (
        vendiaResource.orgId === orgId &&
        vendiaResource.uniName === uniName &&
        vendiaResource.nodeName === nodeName &&
        vendiaResource.entityName === 'Vendia_Table'
      ) {
        return true
      }
    }
  }

  return false
}

/**
 * Add table query access to a role's capabilities (immutably)
 * Adds DATA_READ for Vendia_Table (only if no broader data access exists) and TABLE_ALL for specific table
 */
export function addTableAccessToRole(
  capabilities: Capability[],
  tableResource: VendiaResourceRepresentation,
): Capability[] {
  // Deep clone capabilities to avoid mutating the original
  let newCapabilities: Capability[] = capabilities.map((cap) => ({
    ...cap,
    resources: [...cap.resources],
    conditions: [...(cap.conditions ?? [])],
  }))

  // Parse the table resource to get the base components
  const parsedResource = getVendiaResourceFromResourceString(tableResource)

  // Only add DATA_READ if the role doesn't already have broader data access
  if (!hasExistingDataAccess(newCapabilities, parsedResource.orgId, parsedResource.uniName, parsedResource.nodeName)) {
    const metadataResource = buildTableMetadataResource({
      orgId: parsedResource.orgId,
      uniName: parsedResource.uniName,
      nodeName: parsedResource.nodeName,
    })

    const existingDataReadIndex = newCapabilities.findIndex((c) => c.action === ActionEnum.DataRead)
    if (existingDataReadIndex !== -1) {
      const existing = newCapabilities[existingDataReadIndex]
      // Add resource if not already present
      if (!existing.resources.includes(metadataResource)) {
        newCapabilities[existingDataReadIndex] = {
          ...existing,
          resources: [...existing.resources, metadataResource],
        }
      }
    } else {
      newCapabilities.push({
        action: ActionEnum.DataRead,
        resources: [metadataResource],
        conditions: [],
      })
    }
  }

  // Add TABLE_ALL capability for query access (specific table)
  const existingTableAllIndex = newCapabilities.findIndex((c) => c.action === ActionEnum.TableAll)
  if (existingTableAllIndex !== -1) {
    const existing = newCapabilities[existingTableAllIndex]
    // Add resource if not already present
    if (!existing.resources.includes(tableResource)) {
      newCapabilities[existingTableAllIndex] = {
        ...existing,
        resources: [...existing.resources, tableResource],
      }
    }
  } else {
    newCapabilities.push({
      action: ActionEnum.TableAll,
      resources: [tableResource],
      conditions: [],
    })
  }

  return newCapabilities
}

/**
 * Add entity data access to a role's capabilities (immutably)
 * Adds DATA_READ or DATA_ALL for the specific entity
 * @param capabilities - Current role capabilities
 * @param entityResource - The VendiaResource for the entity
 * @param accessLevel - 'read' for DATA_READ, 'write' for DATA_ALL
 */
export function addEntityAccessToRole(
  capabilities: Capability[],
  entityResource: VendiaResourceRepresentation,
  accessLevel: 'read' | 'write',
): Capability[] {
  // Deep clone capabilities to avoid mutating the original
  let newCapabilities: Capability[] = capabilities.map((cap) => ({
    ...cap,
    resources: [...cap.resources],
    conditions: [...(cap.conditions ?? [])],
  }))

  const action = accessLevel === 'write' ? ActionEnum.DataAll : ActionEnum.DataRead

  const existingIndex = newCapabilities.findIndex((c) => c.action === action)
  if (existingIndex !== -1) {
    const existing = newCapabilities[existingIndex]
    // Add resource if not already present
    if (!existing.resources.includes(entityResource)) {
      newCapabilities[existingIndex] = {
        ...existing,
        resources: [...existing.resources, entityResource],
      }
    }
  } else {
    newCapabilities.push({
      action,
      resources: [entityResource],
      conditions: [],
    })
  }

  return newCapabilities
}

/**
 * Check if a role has a specific DATA_READ permission on Vendia_Table for this node
 * (as opposed to inherited from broader scope)
 */
export function hasSpecificMetadataAccess(
  capabilities: Capability[],
  metadataResource: VendiaResourceRepresentation,
): boolean {
  const dataReadCap = capabilities.find((c) => c.action === ActionEnum.DataRead)
  if (!dataReadCap) return false
  return dataReadCap.resources.includes(metadataResource)
}

/**
 * Remove access to a resource from a role's capabilities
 * @param capabilities - Current role capabilities
 * @param resource - The VendiaResource to remove access for
 * @param actionsToRemove - Array of actions to remove (e.g., [ActionEnum.TableAll, ActionEnum.TableRead])
 */
export function removeResourceAccessFromRole(
  capabilities: Capability[],
  resource: VendiaResourceRepresentation,
  actionsToRemove: ActionEnum[],
): Capability[] {
  return capabilities
    .map((capability) => {
      if (actionsToRemove.includes(capability.action)) {
        const filteredResources = capability.resources.filter((r) => r !== resource)
        if (filteredResources.length === 0) {
          return null
        }
        return { ...capability, resources: filteredResources }
      }
      return capability
    })
    .filter((c): c is Capability => c !== null)
}

/**
 * Remove table query access from a role's capabilities (table-specific wrapper)
 * Removes TABLE_ALL and TABLE_READ for the specific table
 * Optionally also removes DATA_READ on Vendia_Table if alsoRemoveMetadataAccess is true
 */
export function removeTableAccessFromRole(
  capabilities: Capability[],
  tableResource: VendiaResourceRepresentation,
  options?: { alsoRemoveMetadataAccess?: boolean },
): Capability[] {
  // First remove TABLE_ALL and TABLE_READ
  let result = removeResourceAccessFromRole(capabilities, tableResource, [ActionEnum.TableAll, ActionEnum.TableRead])

  // Optionally remove DATA_READ for Vendia_Table metadata
  if (options?.alsoRemoveMetadataAccess) {
    const parsedResource = getVendiaResourceFromResourceString(tableResource)
    const metadataResource = buildTableMetadataResource({
      orgId: parsedResource.orgId,
      uniName: parsedResource.uniName,
      nodeName: parsedResource.nodeName,
    })
    result = removeResourceAccessFromRole(result, metadataResource, [ActionEnum.DataRead])
  }

  return result
}

/**
 * Human-readable label for access source
 * @param source - The access source type
 * @param resourceType - Optional resource type for specific access label ('table' | 'entity')
 */
export function getAccessSourceLabel(source: AccessSource, resourceType?: 'table' | 'entity'): string {
  switch (source) {
    case 'org-wide':
      return 'via org-wide access'
    case 'uni-wide':
      return 'via project-wide access'
    case 'node-wide':
      return 'via node-wide access'
    case 'resource-specific':
      if (resourceType === 'table') return 'direct table access'
      if (resourceType === 'entity') return 'direct entity access'
      return 'direct access'
    case 'none':
      return 'no access'
  }
}

/**
 * Human-readable label for access level
 */
export function getAccessLevelLabel(level: AccessLevel): string {
  switch (level) {
    case 'write':
      return 'Read & Write'
    case 'read':
      return 'Read only'
    case 'none':
      return 'No access'
  }
}

// ============================================================================
// Table-specific convenience exports (for backward compatibility)
// ============================================================================

/** @deprecated Use buildResource instead */
export const buildTableResource = buildResource

/** Table actions for checking table access */
export const TABLE_ACTIONS = [ActionEnum.TableAll, ActionEnum.TableRead]

/** Entity data actions for checking entity access */
export const ENTITY_DATA_ACTIONS = [ActionEnum.DataAll, ActionEnum.DataRead]

/**
 * Normalize capabilities for API calls.
 * Ensures conditions is always an array and handles type mismatch between
 * Capability (conditions: string[]) and API (conditions: ConditionsInput[])
 */
export const normalizeCapabilitiesForApi = (capabilities: Capability[]) => {
  return capabilities.map((cap) => ({
    action: cap.action,
    resources: cap.resources,
    conditions: (cap.conditions ?? []) as any,
  }))
}
